import cache from '../cache';
import { dispatchCustomEvent } from '../utils';
import urls from '../urls';
import OverlayStyleProvider from '../providers/OverlayStyleProvider';
import * as ids from '../layerIdStrings';
import * as eventNames from '../customEventStrings';
import * as uiStrings from '../uiStrings';
import PointMarkupBuilder from '../components/Map/PointMarkupBuilder';
import PopupFactory from '../components/Map/PopupFactory';
import { LOCATIONS_WITH_AUDIO } from '../components/Map/config';
import LoaderManager from './LoaderManager';
import MarkerIconStyleProvider from '../providers/MarkerIconStyleProvider';
import quizManager from './QuizManager';

class MapManager {
	constructor(map) {
		this.map = map;

		// Format:
		// results = {
		//   KOD_OBEC: {
		//     KOD_COB: {
		//       feature: {},
		//       query_ids: [],
		//     }
		//   }
		// }
		this.results = {};
		this.markersLayer = null;
		this.overlaysLayer = null;

		cache.init('layers');
		cache.init('dialects');
		cache.init('obec');
		cache.init('katastralni-uzemi');
		cache.init('rastrs');
		cache.init('array-buffers');
	}

	async openPopupOnMarkerClick(marker, properties) {
		//const popup = L.popup();
		const popup = marker.getPopup();
		//marker.bindPopup(popup);
		popup.setContent(PopupFactory.popupLoaderMarkupBuilder(uiStrings.DOWNLOADING_DATA_MSG));
		popup.update();

		const pointInfoMarkup = await this.getPointInfoMarkup(
			marker.getLatLng(),
			properties.KOD_OBEC,
			properties.KOD_COB
		);


		popup.setContent(`
<div class="popup__content">
${pointInfoMarkup}
</div>
`);
		popup.update();
	}

	async getPointInfoMarkup(latlng, KOD_OBEC = "", KOD_COB = "") {
		const response = await fetch(document.getElementById("appContainer").dataset.pointUrl, {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify({
				lng: latlng.lng,
				lat: latlng.lat,
				KOD_OBEC: KOD_OBEC,
				KOD_COB: KOD_COB,
			}),
		});
		const pointInfo = await response.json();
		return PointMarkupBuilder.getMarkup(pointInfo);
	}

	mountComponent(container) {
		this.map.mount(container);
		this.setupEventListeners();
		this.init();
	}

	setupEventListeners() {
		// TODO: move elsewhere?
		this.map.getMap().addEventListener('click', async e => {
			const popup = L.popup({
				autoPanPaddingTopLeft: L.point(5, 70),
			});
			popup
				.setLatLng(e.latlng)
				.setContent(PopupFactory.popupLoaderMarkupBuilder(uiStrings.DOWNLOADING_DATA_MSG))
				.openOn(this.map.getMap());

			const pointInfoMarkup = await this.getPointInfoMarkup(e.latlng);
			popup.setContent(`<div class="popup__content">` + pointInfoMarkup + `</div>`);
		});
	}

	init() {
	}

	addLayer(props) {
		if (props.id == ids.DIALECTS_COARSE || props.id == ids.DIALECTS_FINE || props.id == ids.DIALECTS_NONE) {
			this.addDialects(props);
		} else if (props.type === 'overlay') {
			this.addOverlay(props);
		} else if (props.type === 'markers') {
			this.addMarkers(props);
		}
	}

	redrawMarkers() {
		const markers = this.createMarkerGroup();

		if (this.markersLayer != null) {
			this.map.removeLayer(this.markersLayer);
		}

		this.markersLayer = markers;
		this.displayLayer(markers);
	}

	redrawOverlays(overlays) {
		if (this.overlaysLayer != null) {
			this.map.removeLayer(this.overlaysLayer);
		}

		this.overlaysLayer = overlays;
		this.displayLayer(overlays);
	}
	/*
    async refreshMarkers() {
        LoaderManager.mountLoader('downloading');

        this.results = {};

        const queryKeys = this.getQueryKeys();

        let query = [];

        for (const queryKey of queryKeys) {
            query.push({
                key: queryKey,
                filter: this.getFilterDialectIds(queryKey),
            });
        }

        await this.createMarkers(query, urls.marker);
        LoaderManager.unmountLoader();

        this.redrawMarkers();
    }
*/
	removeLayer(props) {
		if (props.type === 'overlay') {
			this.removeOverlay(props.id);
		} else if (props.type === 'markers') {
			this.removeMarkers(props.id);
		}
	}

	removeMarkers(id) {
		// Remove query id from this.results[*][*].query_ids and update marker layer
		for (const KOD_OBEC in this.results) {
			for (const KOD_COB in this.results[KOD_OBEC]) {
				const index = this.results[KOD_OBEC][KOD_COB].query_ids.indexOf(id);

				if (index > -1) {
					//this.results[KOD_OBEC][KOD_COB].query_ids.splice(index, 1);
					this.results[KOD_OBEC][KOD_COB].query_ids = this.results[KOD_OBEC][KOD_COB].query_ids.filter(item => item !== id);

					if (this.results[KOD_OBEC][KOD_COB].query_ids.length == 0) {
						delete this.results[KOD_OBEC][KOD_COB];
					}
				}
			}
		}

		this.redrawMarkers();
	}

	removeOverlay(id) {
		const cached = cache.has('layers', id);

		if (cached) {
			this.map.removeLayer(cached);
		}
	}

	dispatchRemoveLayerPanelLoader(id) {
		dispatchCustomEvent(eventNames.REMOVE_LAYER_PANEL_LOADER, document, false, {id});
	}

	async addOverlay(props) {
		// loader
		LoaderManager.mountLoader('downloading');

		let cached = cache.has('layers', props.id);
		if (!cached) {
			cached = await this.createOverlay({ id: props.id }, urls.polygon, props.id);

			cache.save('layers', props.id, cached);
		}

		LoaderManager.unmountLoader();
		this.dispatchRemoveLayerPanelLoader(props.id);

		// display
		this.displayLayer(cached);
	}

	async addDialects(data) {
		let layers = [];

		if (data.id != ids.DIALECTS_NONE) {
			// loader
			LoaderManager.mountLoader('downloading');

			let dialectIds = []
			const dialects = JSON.parse(document.getElementById("appContainer").dataset.dialects);

			if (data.id == ids.DIALECTS_COARSE) {
				for (let i in dialects) {
					const dialect = dialects[i];

					if (dialect.level == 'oblast') {
						dialectIds.push(dialect.id);
					}
				}
			} else {
				dialectIds = this.getFilterDialectIds(ids.DIALECTS_FINE);

				if (dialectIds.length == 0) {
					for (let i in dialects) {
						const dialect = dialects[i];

						if (dialect.has_boundary == "1" && dialect.level != "region") {
							dialectIds.push(dialect.id);
						}
					}
					//dialectIds = Object.keys(dialects);
				}
			}

			let reqDialectIds = [];

			for (let dialectId of dialectIds) {
				let layer = cache.has('dialects', dialectId);

				if (layer) {
					layers.push(layer);
				} else {
					reqDialectIds.push(dialectId);
				}
			}

			if (reqDialectIds.length > 0) {
				layers.push(await this.createOverlay(
					{
						id: data.id,
						filter: reqDialectIds,
					},
					urls.polygon,
					data.id
				));
			}

			LoaderManager.unmountLoader();
		}

		this.dispatchRemoveLayerPanelLoader(data.id);

		// display
		this.redrawOverlays(L.layerGroup(layers));
	}

	async createOverlay(data, url, id) {
		const json = await this.fetchGeojson(data, url);

		let geoJsonLayer;

		let options = {
			style: OverlayStyleProvider.getStyle(id),
			onEachFeature: function (feature, layer) {
				if (id == ids.DIALECTS_COARSE || id == ids.DIALECTS_FINE) {
					cache.save('dialects', feature.properties.id, layer);
					/*
                    layer.on('mouseover', function () {
                        this.setStyle({
                            weight: 3,
                            fillOpacity: 0.7,
                        });
                    });
                    layer.on('mouseout', function () {
                        geoJsonLayer.resetStyle(this);
                    });
                    */
				}
			},
		};

		// do we have a separate pane for this layer?
		if (id in this.map.getMap().getPanes()) {
			options.pane = id;
		}

		geoJsonLayer = L.geoJson(json, options);
		return geoJsonLayer;
	}

	displayLayer(layer) {
		this.map.addLayer(layer);
	}

	async fetchGeojson(data, url) {
		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify(data),
			});

			return await response.json();

		} catch (error) {
			console.error(error);
		}
	}

	// MARKERS
	async addMarkers(props) {
		this.results = {}; //PMa hack - seems like a correct way to clear array. Alternative - set .length = 0

		LoaderManager.mountLoader('downloading');

		await this.createMarkers([{ key: props.id, filter: this.getFilterDialectIds(props.id) }], urls.marker);

		LoaderManager.unmountLoader();
		this.dispatchRemoveLayerPanelLoader(props.id);

		this.redrawMarkers();
	}

	createMarkerGroup() {
		let features = [];

		for (const KOD_OBEC in this.results) {
			for (const KOD_COB in this.results[KOD_OBEC]) {
				features.push(this.results[KOD_OBEC][KOD_COB].feature);
			}
		}

		const markers = L.markerClusterGroup({
			maxClusterRadius: 20,
		});

		const geoJsonLayer = L.geoJson({features: features}, {
			pointToLayer: (feature, latlng) => {
				const KOD_OBEC = feature.properties.KOD_OBEC;
				const KOD_COB = feature.properties.KOD_COB;
				let resultCount = this.results[KOD_OBEC][KOD_COB].query_ids.length;
				let gradientStops = [];

				for (let i = 0; i < resultCount; i++) {
					//PMa: specialne farby markerov pocas kvizu
					var color = MarkerIconStyleProvider.getColor(this.results[KOD_OBEC][KOD_COB].query_ids[i]);
					if (quizManager.isFinished()) {
						const exampleId = this.results[KOD_OBEC][KOD_COB].feature.properties.EXAMPLE_ID;
						const evaluation = quizManager.getEvaluatedAnswer(exampleId);
						if (evaluation && evaluation.points === evaluation.possiblePoints) {
							color = MarkerIconStyleProvider.getColor('evaluated_quiz_good_color');
						} else if (evaluation) {
							color = MarkerIconStyleProvider.getColor('evaluated_quiz_bad_color');
						}
					}
					gradientStops.push(color + " " + ((360 / resultCount) * i) + "deg " + ((360 / resultCount) * (i + 1)) + "deg");
				}

				let title = feature.properties.NAZ_OBEC;

				if (feature.properties.NAZ_COB != '') {
					title = feature.properties.NAZ_COB + ', ' + title;
				}

				let iconHtml = '';

				if (this.results[KOD_OBEC][KOD_COB].query_ids.some(query_id => LOCATIONS_WITH_AUDIO.includes(query_id))) {
					//PMa: vyberajme vhodnu ikonu podla fazy kvizu a stavu konkretneho spendlika
					iconHtml = '<i class="fas fa-question fa-3x"></i>';
					const exampleId = this.results[KOD_OBEC][KOD_COB].feature.properties.EXAMPLE_ID;
					if (quizManager.isRunning()) {
						//ak kviz bezi, spendliky kde je uz vyklikana odpoved dajme bodku
						if (quizManager.hasAnswer(exampleId)) {
							iconHtml = '<i class="fas fa-circle"></i>';
						}
					} else if (quizManager.isFinished()) {
						//ak kviz dobehol, zobrazujeme vyhodnotenie - dobre a zle odpovede
						const evaluation = quizManager.getEvaluatedAnswer(exampleId);
						if (evaluation && evaluation.points === evaluation.possiblePoints) {
							iconHtml = '<i class="fas fa-thumbs-up"></i>';
						} else if (evaluation) {
							iconHtml = '<i class="fas fa-times"></i>';
						}
					}
				}

				let iconProps = {
					markerColor: 'white',
					innerHTML: '<div class="marker" style="background: conic-gradient(' + gradientStops.join(", ") + ');">'+iconHtml+'</div>',
				};

				if (this.getQueryKeys().length == 1 && this.results[KOD_OBEC][KOD_COB].query_ids[0] == 'cja_place') {
					/*iconProps = {
                        icon: 'fa-number',
                        //iconColor,
                        number: feature.properties.cja_id.padStart(2, '0'),
                        markerColor: MarkerIconStyleProvider.getColor('cja_place'),
                        shape: 'square',
                        prefix: 'fas',
                        svg: true,
                    };*/
					iconProps = MarkerIconStyleProvider.getCjaIconProps(feature.properties.cja_id);
				}

				return L.marker(latlng, {
					icon: L.ExtraMarkers.icon(iconProps),
					title: title,
					riseOnHover: true
				});
			},
			onEachFeature: function (feature, layer) {
				layer.bindPopup("", {
					autoPanPaddingTopLeft: L.point(5, 70),
				});
				layer.addEventListener('click', e => {
					dispatchCustomEvent(eventNames.MARKER_CLICKED, document, false, {
						marker: e.target,
						properties: feature.properties,
					});
				});
			}
		});
		markers.addLayer(geoJsonLayer);

		return markers;
	}

	async createMarkers(query, url) {
		const json = await this.fetchGeojson(query, url);

		for (let i = 0; i < json.features.length; i++) {
			const feature = json.features[i];
			const key = feature.properties.key;
			const KOD_OBEC = feature.properties.KOD_OBEC;
			const KOD_COB = feature.properties.KOD_COB;

			if (!(KOD_OBEC in this.results)) {
				this.results[KOD_OBEC] = {};
			}

			if (!(KOD_COB in this.results[KOD_OBEC])) {
				this.results[KOD_OBEC][KOD_COB] = {
					feature: feature,
					query_ids: [],
				};
			}

			if (feature.properties.cja_id) {
				this.results[KOD_OBEC][KOD_COB].feature.properties.cja_id = feature.properties.cja_id;
			}

			const index = this.results[KOD_OBEC][KOD_COB].query_ids.indexOf(key);

			if (index == -1) {
				this.results[KOD_OBEC][KOD_COB].query_ids.push(key);
			}
		}

		quizManager.setExampleIDsByMarkers(json);
	}

	// RELIEF
	async addRastrLayer(id) {
		let cached = cache.has('rastrs', id);
		if (!cached) {
			cached = await this.createRastr(id);

			cache.save('rastrs', id, cached);
		}

		// display
		this.displayLayer(cached);
	}

	async createRastr(id) {
		// cache geojson, filters use the same file - only fetch once
		let arrayBuffer = cache.has('array-buffers', id);

		LoaderManager.mountLoader('downloading-rastr');

		if (!arrayBuffer) {
			arrayBuffer = await this.fetchArrayBuffer();
			cache.save('array-buffers', id, arrayBuffer);
		}

		LoaderManager.updateLoaderMessage('parsing-rastr');

		const georaster = await this.parseArrayBuffer(arrayBuffer);
		LoaderManager.unmountLoader();
		this.dispatchRemoveLayerPanelLoader(id);

		return new GeoRasterLayer({
			georaster: georaster,
			opacity: 0.3,
			resolution: 200,
			updateWhenIdle: true,
		});
	}

	async fetchArrayBuffer() {
		try {
			const response = await fetch(urls.relief, {
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
				},
			});

			return await response.arrayBuffer();

		} catch (error) {
			console.error(error);
		}
	}

	async parseArrayBuffer(arrayBuffer) {
		return await parseGeoraster(arrayBuffer);
	}

	removeRastrLayer(id) {
		const cached = cache.has('rastrs', id);
		if (cached) {
			this.map.removeLayer(cached);
		}
	}

	printMap() {
		this.map.printMap();
	}

	async onSearchRemoved(id, feature) {
		const KOD_OBEC = feature.properties.KOD_OBEC;
		const KOD_COB = feature.properties.KOD_COB;

		// Remove query id from this.results[KOD_OBEC][KOD_COB].query_ids and update marker layer
		this.results[KOD_OBEC][KOD_COB].query_ids = this.results[KOD_OBEC][KOD_COB].query_ids.filter(item => item !== id);

		if (this.results[KOD_OBEC][KOD_COB].query_ids.length == 0) {
			delete this.results[KOD_OBEC][KOD_COB];
		}

		this.redrawMarkers();

		const type = (KOD_COB == "") ? 'obec' : 'katastralni-uzemi';
		const code = (KOD_COB == "") ? KOD_OBEC : KOD_COB;

		const layer = cache.has(type, code);

		if (layer) {
			this.map.removeLayer(layer);
		}
	}

	async onSearchSelected(id, data, flyTo) {
		//LoaderManager.mountLoader('downloading');

		const feature = {
			type: "Feature",
			properties: data.properties,
			geometry: data.geometry,
		};
		const KOD_OBEC = feature.properties.KOD_OBEC;
		const KOD_COB = feature.properties.KOD_COB;

		if (!(KOD_OBEC in this.results)) {
			this.results[KOD_OBEC] = {};
		}

		if (!(KOD_COB in this.results[KOD_OBEC])) {
			this.results[KOD_OBEC][KOD_COB] = {
				feature: feature,
				query_ids: [],
			};
		}

		const index = this.results[KOD_OBEC][KOD_COB].query_ids.indexOf(id);

		if (index == -1) {
			this.results[KOD_OBEC][KOD_COB].query_ids.push(id);
		}

		//LoaderManager.unmountLoader();
		//this.dispatchRemoveLayerPanelLoader(props.id);

		this.redrawMarkers();

		if (flyTo) {
			const coords = feature.geometry.coordinates;
			this.map.getMap().flyTo(L.latLng(coords[1], coords[0]), 10);
		}

		const type = (KOD_COB == "") ? 'obec' : 'katastralni-uzemi';
		const code = (KOD_COB == "") ? KOD_OBEC : KOD_COB;

		if (document.querySelector(".js-search-with-bounds-checkbox").checked) {
			let layer = cache.has(type, code);

			if (!layer) {
				layer = await this.createObcePolygon(KOD_OBEC, KOD_COB);
				cache.save(type, code, layer);
			}

			this.displayLayer(layer);
		}
	}

	async onSearchWithBoundsChange(checked) {
		for (const KOD_OBEC in this.results) {
			for (const KOD_COB in this.results[KOD_OBEC]) {
				const searchResultIds = this.results[KOD_OBEC][KOD_COB].query_ids.filter(id => id.startsWith("search-result-"));

				for (const searchResultId of searchResultIds) {
					const type = (KOD_COB == "") ? 'obec' : 'katastralni-uzemi';
					const code = (KOD_COB == "") ? KOD_OBEC : KOD_COB;

					let layer = cache.has(type, code);

					if (checked) {
						// Add bounds
						if (!layer) {
							layer = await this.createObcePolygon(KOD_OBEC, KOD_COB);
							cache.save(type, code, layer);
						}

						this.displayLayer(layer);
					} else {
						// Remove bounds
						if (layer) {
							this.map.removeLayer(layer);
						}
					}
				}
			}
		}
	}

	async createObcePolygon(KOD_OBEC, KOD_COB) {
		const json = await this.fetchGeojson({KOD_OBEC: KOD_OBEC, KOD_COB: KOD_COB}, urls.polygon);

		return L.geoJson(json, {
			style: OverlayStyleProvider.getStyle("obec"),
		});
	}

	getFilterDialectIds(key) {
		const row = document.querySelector('[ref="row-'+key+'"]');
		const checkboxes = row.nextElementSibling.querySelectorAll('input[type="checkbox"]:checked');
		let dialectIds = [];

		for (const checkbox of checkboxes) {
			dialectIds.push(parseInt(checkbox.value));
		}

		return dialectIds;
	}

	// TODO: use for import
	getQueryKeys() {
		const checkboxes = document.querySelectorAll('.js-layers-markers input.js-query-picker[type="checkbox"]:checked');
		let keys = [];

		for (const checkbox of checkboxes) {
			keys.push(checkbox.value);
		}

		return keys;
	}

	saveFile() {
		const searchResults = document.querySelector('.js-search-results');
		let results = [];

		for (const searchResult of searchResults.children) {
			const properties = JSON.parse(searchResult.dataset.feature).properties;

			let result = {
				KOD_OBEC: properties.KOD_OBEC,
			};

			if (properties.KOD_COB != "") {
				result.KOD_COB = properties.KOD_COB;
			}

			result.color = MarkerIconStyleProvider.getColor(searchResult.id);

			results.push(result);
		}

		//const checkboxes = document.querySelectorAll('.js-layers-markers input.js-query-picker[type="checkbox"]');
		const checkboxes = document.querySelectorAll('.js-query-picker');
		let queries = {};

		for (const checkbox of checkboxes) {
			const queryId = checkbox.value;

			queries[queryId] = {
				on: checkbox.checked,
				color: MarkerIconStyleProvider.getColor(queryId),
			};

			const row = checkbox.closest(`[ref="row-${queryId}"]`);

			if (row) {
				const dialectPicker = row.nextElementSibling;

				if (dialectPicker) {
					const dialectIds = dialectPicker.classList.contains('js-dialect-picker') ? this.getFilterDialectIds(queryId) : [];

					if (dialectIds.length > 0) {
						queries[queryId].dialect_ids = dialectIds;
					}
				}
			}
		}

		const fileName = `mapka_${Date.now()}.json`;

		const data = {
			format: 3,
			search: {
				results: results,
				showBounds: document.querySelector(".js-search-with-bounds-checkbox").checked,
			},
			query: queries,
		};

		let link = document.createElement("a");
		link.href = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, "\t"))}`;
		link.download = fileName;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	loadFile(file) {
		const fr = new FileReader();

		fr.addEventListener("load", async (e) => {
			const data = JSON.parse(e.target.result);

			if ("format" in data && data.format == 3) {
				try {
					dispatchCustomEvent(
						'search-init',
						document,
						true,
						{
							showBounds: data.search.showBounds,
						}
					);

					for (let queryId in data.query) {
						const query = data.query[queryId];
						const checkbox = document.querySelector(`.js-query-picker[value="${queryId}"]`);

						if (query.color) {
							MarkerIconStyleProvider.setColor(queryId, query.color);
							dispatchCustomEvent(
								eventNames.CHANGE_CUSTOM_MARKER_COLOR,
								document,
								true,
								{ id: queryId, color: query.color }
							);
						}

						if (query.dialect_ids) {
							const dialectsPicker = document.getElementById(queryId).nextElementSibling;

							dialectsPicker.querySelectorAll('input[type="checkbox"]').forEach(cb => {
								cb.checked = query.dialect_ids.includes(parseInt(cb.value));
							});
						}

						if (checkbox.checked != query.on) {
							document.querySelector(`[ref="checkbox-${queryId}"]`).click();
						}
					}

					for (let searchResult of data.search.results) {
						dispatchCustomEvent(
							'search-result-load',
							document,
							true,
							{
								KOD_OBEC: searchResult.KOD_OBEC,
								KOD_COB: searchResult.KOD_COB ? searchResult.KOD_COB : "",
								color: searchResult.color,
							}
						);
					}
				} catch (error) {
					console.error(error);
				}
			} else {
				console.log("Import: unknown file format")
			}

			/*
            const borderPromises = [];

            for (let i = 0; i < markersData.length; i++) {
                borderPromises.push(this.createObcePolygon(id));
            }

            LoaderManager.mountLoader('uploading-file');
            const borders = await Promise.allSettled(borderPromises);
            LoaderManager.unmountLoader();

            borders.forEach((settled, index) => {
                const border = settled.value;
                border && this.displayLayer(border);
            });
            */
		});

		fr.readAsText(file);
	}

	exportFile() {
		let features = [];

		for (const KOD_OBEC in this.results) {
			for (const KOD_COB in this.results[KOD_OBEC]) {
				const result = this.results[KOD_OBEC][KOD_COB];
				let feature = result.feature;

				// Simplestyle properties according to
				// https://github.com/mapbox/simplestyle-spec/tree/master/1.1.0

				// Title
				let title = feature.properties.NAZ_OBEC;

				if (feature.properties.NAZ_COB != '') {
					title = feature.properties.NAZ_COB + ', ' + title;
				}

				feature.properties.title = title;

				// Marker color
				const resultCount = result.query_ids.length;

				if (resultCount == 1) {
					feature.properties["marker-color"] = MarkerIconStyleProvider.getColor(this.results[KOD_OBEC][KOD_COB].query_ids[0])
				}

				// Remove "key"
				delete feature.properties.key;

				features.push(feature);
			}
		}

		const fileName = `mapka_${Date.now()}.geojson`;

		const data = {
			type: "FeatureCollection",
			name: fileName,
			crs: {
				type: "name",
				properties: {
					name: "urn:ogc:def:crs:OGC:1.3:CRS84",
				},
			},
			features: features,
		};

		let link = document.createElement("a");
		link.href = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, "\t"))}`;
		link.download = fileName;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	closePopup() {
		this.map.getMap().closePopup();
	}

	zoomIn() {
		this.map.getMap().zoomIn();
	}

	zoomOut() {
		this.map.getMap().zoomOut();
	}
}

export default MapManager;
